import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslate } from 'piral-translate';

import { useKeycloak } from '../../keycloak-api/useKeycloak';
import { Breadcrumbs } from 'piral-breadcrumbs';
import { ExtensionSlot } from 'piral-core';
import { LoginButton } from '../LoginButton/LoginButton';
import { ProfileButton } from '../ProfileButton/ProfileButton';
import { SipLogo } from '../SipLogo/SipLogo';
import { SpotlightTrigger } from '../Spotlight/SpotlightTrigger';
import { ControlCenter } from '../ControlCenter/ControlCenter';

import './header.css';

// Daylight arc graph component
interface DaylightArcProps {
  sunrise: Date;
  sunset: Date;
  currentTime: Date;
  formatTime: (date: Date) => string;
}

const DaylightArc: React.FC<DaylightArcProps> = ({ sunrise, sunset, currentTime, formatTime }) => {
  const width = 320;
  const height = 110;
  const padding = 30;
  const arcWidth = 125; // Half-width of the arc (horizontal radius)
  const arcHeight = 65; // Height of the arc (vertical radius) - smaller = flatter
  const centerX = width / 2;
  const centerY = height - 20;

  // Calculate sun position (0 = sunrise, 1 = sunset)
  const sunriseTime = sunrise.getTime();
  const sunsetTime = sunset.getTime();
  const now = currentTime.getTime();

  const isDay = now >= sunriseTime && now <= sunsetTime;
  const isBeforeSunrise = now < sunriseTime;
  const isAfterSunset = now > sunsetTime;

  let progress = 0;
  if (isBeforeSunrise) {
    // Before sunrise - sun rests at the start of the arc (position 0)
    progress = 0;
  } else if (isAfterSunset) {
    // After sunset - sun rests at the end of the arc (position 1)
    progress = 1;
  } else {
    // During day - calculate position on arc
    progress = (now - sunriseTime) / (sunsetTime - sunriseTime);
  }

  // Calculate sun position on the elliptical arc (from left to right)
  const angle = Math.PI * (1 - progress); // PI to 0 (left to right)
  const sunX = centerX + arcWidth * Math.cos(angle);
  const sunY = centerY - arcHeight * Math.sin(angle);

  // Arc path (elliptical arc - flatter than semicircle)
  const arcPath = `M ${centerX - arcWidth} ${centerY} A ${arcWidth} ${arcHeight} 0 0 1 ${centerX + arcWidth} ${centerY}`;

  return (
    <svg width={width} height={height} className="neo-daylight-arc">
      {/* Horizon line */}
      <line
        x1={padding}
        y1={centerY}
        x2={width - padding}
        y2={centerY}
        className="neo-horizon-line"
      />

      {/* Day arc (dashed) */}
      <path
        d={arcPath}
        fill="none"
        className="neo-arc-path"
      />

      {/* Sunrise marker */}
      <circle cx={centerX - arcWidth} cy={centerY} r="4" className="neo-arc-marker sunrise" />
      <text x={centerX - arcWidth} y={centerY + 20} className="neo-arc-label" textAnchor="middle">
        {formatTime(sunrise)}
      </text>

      {/* Sunset marker */}
      <circle cx={centerX + arcWidth} cy={centerY} r="4" className="neo-arc-marker sunset" />
      <text x={centerX + arcWidth} y={centerY + 20} className="neo-arc-label" textAnchor="middle">
        {formatTime(sunset)}
      </text>

      {/* Sun position - always visible, styled differently when not daytime */}
      <g className={`neo-sun-marker ${!isDay ? 'night' : ''}`}>
        <circle cx={sunX} cy={sunY} r="12" className="neo-sun-glow" />
        <circle cx={sunX} cy={sunY} r="8" className="neo-sun-circle" />
      </g>
    </svg>
  );
};

// Moon phase arc component
interface MoonPhaseArcProps {
  moonrise: Date;
  moonset: Date;
  currentTime: Date;
  formatTime: (date: Date) => string;
}

const MoonPhaseArc: React.FC<MoonPhaseArcProps> = ({ moonrise, moonset, currentTime, formatTime }) => {
  const width = 320;
  const height = 110;
  const arcWidth = 125;
  const arcHeight = 65;
  const centerX = width / 2;
  const centerY = height - 20;

  const moonriseTime = moonrise.getTime();
  const moonsetTime = moonset.getTime();
  const now = currentTime.getTime();

  // Handle case where moonset is before moonrise (moon is up overnight)
  const adjustedMoonsetTime = moonsetTime < moonriseTime ? moonsetTime + 24 * 60 * 60 * 1000 : moonsetTime;
  const adjustedNow = now < moonriseTime && moonsetTime < moonriseTime ? now + 24 * 60 * 60 * 1000 : now;

  const isMoonUp = adjustedNow >= moonriseTime && adjustedNow <= adjustedMoonsetTime;
  const isBeforeMoonrise = adjustedNow < moonriseTime;
  const isAfterMoonset = adjustedNow > adjustedMoonsetTime;

  let progress = 0;
  if (isBeforeMoonrise) {
    progress = 0;
  } else if (isAfterMoonset) {
    progress = 1;
  } else {
    progress = (adjustedNow - moonriseTime) / (adjustedMoonsetTime - moonriseTime);
  }

  const angle = Math.PI * (1 - progress);
  const moonX = centerX + arcWidth * Math.cos(angle);
  const moonY = centerY - arcHeight * Math.sin(angle);

  const arcPath = `M ${centerX - arcWidth} ${centerY} A ${arcWidth} ${arcHeight} 0 0 1 ${centerX + arcWidth} ${centerY}`;

  return (
    <svg width={width} height={height} className="neo-daylight-arc neo-moon-arc">
      {/* Horizon line */}
      <line x1={30} y1={centerY} x2={width - 30} y2={centerY} className="neo-horizon-line" />

      {/* Moon arc (dashed) */}
      <path d={arcPath} fill="none" className="neo-arc-path neo-moon-arc-path" />

      {/* Moonrise marker */}
      <circle cx={centerX - arcWidth} cy={centerY} r="4" className="neo-arc-marker moonrise" />
      <text x={centerX - arcWidth} y={centerY + 20} className="neo-arc-label" textAnchor="middle">
        {formatTime(moonrise)}
      </text>

      {/* Moonset marker */}
      <circle cx={centerX + arcWidth} cy={centerY} r="4" className="neo-arc-marker moonset" />
      <text x={centerX + arcWidth} y={centerY + 20} className="neo-arc-label" textAnchor="middle">
        {formatTime(moonset)}
      </text>

      {/* Moon position - same style as sun but gray */}
      <g className={`neo-moon-marker ${!isMoonUp ? 'below-horizon' : ''}`}>
        <circle cx={moonX} cy={moonY} r="12" className="neo-moon-glow" />
        <circle cx={moonX} cy={moonY} r="8" className="neo-moon-circle" />
      </g>
    </svg>
  );
};

// Coordinates for different language regions (hasDST indicates if DST is observed)
const LANGUAGE_COORDINATES: Record<string, { lat: number; lng: number; name: string; hasDST: boolean }> = {
  de: { lat: 48.1351, lng: 11.5820, name: 'Germany', hasDST: true },
  en: { lat: 48.1351, lng: 11.5820, name: 'Germany', hasDST: true },
  es: { lat: 40.4168, lng: -3.7038, name: 'Spain', hasDST: true },
  pl: { lat: 52.2297, lng: 21.0122, name: 'Poland', hasDST: true },
  it: { lat: 41.9028, lng: 12.4964, name: 'Italy', hasDST: true },
  ro: { lat: 44.4268, lng: 26.1025, name: 'Romania', hasDST: true },
  pt: { lat: 38.7223, lng: -9.1393, name: 'Portugal', hasDST: true },
};

// Check if a date is in DST (compares timezone offset with January)
function isDST(date: Date): boolean {
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  return date.getTimezoneOffset() < stdOffset;
}

// Get next DST change date and direction
function getNextDSTChange(date: Date): { date: Date; isSummerToWinter: boolean } | null {
  const year = date.getFullYear();

  // EU DST: Last Sunday of March (to summer) and last Sunday of October (to winter)
  // Find last Sunday of March
  const march = new Date(year, 2, 31); // March 31
  while (march.getDay() !== 0) march.setDate(march.getDate() - 1);
  march.setHours(2, 0, 0, 0); // DST change at 2:00 AM

  // Find last Sunday of October
  const october = new Date(year, 9, 31); // October 31
  while (october.getDay() !== 0) october.setDate(october.getDate() - 1);
  october.setHours(3, 0, 0, 0); // DST change at 3:00 AM (summer time)

  // Determine which is next
  if (date < march) {
    return { date: march, isSummerToWinter: false }; // Winter to Summer (+1h)
  } else if (date < october) {
    return { date: october, isSummerToWinter: true }; // Summer to Winter (-1h)
  } else {
    // Next year's March
    const nextMarch = new Date(year + 1, 2, 31);
    while (nextMarch.getDay() !== 0) nextMarch.setDate(nextMarch.getDate() - 1);
    nextMarch.setHours(2, 0, 0, 0);
    return { date: nextMarch, isSummerToWinter: false };
  }
}

// Helper: Convert degrees to radians
const toRad = (deg: number) => deg * Math.PI / 180;
// Helper: Convert radians to degrees
const toDeg = (rad: number) => rad * 180 / Math.PI;
// Helper: Normalize angle to 0-360
const normalize360 = (deg: number) => ((deg % 360) + 360) % 360;

// Calculate Julian Date from a JavaScript Date
function getJulianDate(date: Date): number {
  const time = date.getTime();
  return time / 86400000 + 2440587.5;
}

// Calculate moon position using more accurate algorithms (based on Jean Meeus)
function getMoonPosition(jd: number): { rightAscension: number; declination: number; distance: number } {
  // Time in Julian centuries from J2000.0
  const T = (jd - 2451545.0) / 36525;

  // Moon's mean longitude
  const Lp = normalize360(218.3164477 + 481267.88123421 * T - 0.0015786 * T * T);

  // Mean elongation of the Moon
  const D = normalize360(297.8501921 + 445267.1114034 * T - 0.0018819 * T * T);

  // Sun's mean anomaly
  const M = normalize360(357.5291092 + 35999.0502909 * T - 0.0001536 * T * T);

  // Moon's mean anomaly
  const Mp = normalize360(134.9633964 + 477198.8675055 * T + 0.0087414 * T * T);

  // Moon's argument of latitude
  const F = normalize360(93.2720950 + 483202.0175233 * T - 0.0036539 * T * T);

  // Longitude corrections (main terms)
  const sumL =
    6288774 * Math.sin(toRad(Mp)) +
    1274027 * Math.sin(toRad(2 * D - Mp)) +
    658314 * Math.sin(toRad(2 * D)) +
    213618 * Math.sin(toRad(2 * Mp)) +
    -185116 * Math.sin(toRad(M)) +
    -114332 * Math.sin(toRad(2 * F)) +
    58793 * Math.sin(toRad(2 * D - 2 * Mp)) +
    57066 * Math.sin(toRad(2 * D - M - Mp)) +
    53322 * Math.sin(toRad(2 * D + Mp)) +
    45758 * Math.sin(toRad(2 * D - M)) +
    -40923 * Math.sin(toRad(M - Mp)) +
    -34720 * Math.sin(toRad(D)) +
    -30383 * Math.sin(toRad(M + Mp)) +
    15327 * Math.sin(toRad(2 * D - 2 * F)) +
    -12528 * Math.sin(toRad(Mp + 2 * F)) +
    10980 * Math.sin(toRad(Mp - 2 * F));

  // Latitude corrections (main terms)
  const sumB =
    5128122 * Math.sin(toRad(F)) +
    280602 * Math.sin(toRad(Mp + F)) +
    277693 * Math.sin(toRad(Mp - F)) +
    173237 * Math.sin(toRad(2 * D - F)) +
    55413 * Math.sin(toRad(2 * D - Mp + F)) +
    46271 * Math.sin(toRad(2 * D - Mp - F)) +
    32573 * Math.sin(toRad(2 * D + F)) +
    17198 * Math.sin(toRad(2 * Mp + F)) +
    9266 * Math.sin(toRad(2 * D + Mp - F)) +
    8822 * Math.sin(toRad(2 * Mp - F));

  // Distance corrections (main terms)
  const sumR =
    -20905355 * Math.cos(toRad(Mp)) +
    -3699111 * Math.cos(toRad(2 * D - Mp)) +
    -2955968 * Math.cos(toRad(2 * D)) +
    -569925 * Math.cos(toRad(2 * Mp)) +
    48888 * Math.cos(toRad(M)) +
    -3149 * Math.cos(toRad(2 * F)) +
    246158 * Math.cos(toRad(2 * D - 2 * Mp)) +
    -152138 * Math.cos(toRad(2 * D - M - Mp)) +
    -170733 * Math.cos(toRad(2 * D + Mp)) +
    -204586 * Math.cos(toRad(2 * D - M));

  // Geocentric ecliptic coordinates
  const lambda = Lp + sumL / 1000000; // Ecliptic longitude
  const beta = sumB / 1000000; // Ecliptic latitude
  const distance = 385000.56 + sumR / 1000; // Distance in km

  // Obliquity of the ecliptic
  const epsilon = 23.439291 - 0.0130042 * T;

  // Convert to equatorial coordinates
  const lambdaRad = toRad(lambda);
  const betaRad = toRad(beta);
  const epsilonRad = toRad(epsilon);

  const rightAscension = toDeg(Math.atan2(
    Math.sin(lambdaRad) * Math.cos(epsilonRad) - Math.tan(betaRad) * Math.sin(epsilonRad),
    Math.cos(lambdaRad)
  ));

  const declination = toDeg(Math.asin(
    Math.sin(betaRad) * Math.cos(epsilonRad) +
    Math.cos(betaRad) * Math.sin(epsilonRad) * Math.sin(lambdaRad)
  ));

  return { rightAscension: normalize360(rightAscension), declination, distance };
}

// Calculate sun position (for illumination calculation)
function getSunPosition(jd: number): { rightAscension: number; declination: number } {
  const T = (jd - 2451545.0) / 36525;

  // Mean longitude of the Sun
  const L0 = normalize360(280.46646 + 36000.76983 * T + 0.0003032 * T * T);

  // Mean anomaly of the Sun
  const M = normalize360(357.52911 + 35999.05029 * T - 0.0001537 * T * T);

  // Equation of center
  const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(toRad(M)) +
    (0.019993 - 0.000101 * T) * Math.sin(toRad(2 * M)) +
    0.000289 * Math.sin(toRad(3 * M));

  // True longitude
  const sunLong = L0 + C;

  // Obliquity
  const epsilon = 23.439291 - 0.0130042 * T;

  const lambdaRad = toRad(sunLong);
  const epsilonRad = toRad(epsilon);

  const rightAscension = toDeg(Math.atan2(
    Math.sin(lambdaRad) * Math.cos(epsilonRad),
    Math.cos(lambdaRad)
  ));

  const declination = toDeg(Math.asin(Math.sin(epsilonRad) * Math.sin(lambdaRad)));

  return { rightAscension: normalize360(rightAscension), declination };
}

// Calculate accurate moon phase
function calculateMoonPhase(date: Date): { phase: number; phaseName: string; illumination: number } {
  const jd = getJulianDate(date);

  const moon = getMoonPosition(jd);
  const sun = getSunPosition(jd);

  // Phase angle (elongation between moon and sun)
  let elongation = moon.rightAscension - sun.rightAscension;
  if (elongation < 0) elongation += 360;

  // Phase as 0-1 (0 = new moon, 0.5 = full moon)
  const phase = elongation / 360;

  // Illumination using the more accurate formula
  // Based on the angular separation between sun and moon
  const phaseAngle = toRad(180 - elongation);
  const illumination = (1 + Math.cos(phaseAngle)) / 2;

  // Determine phase name
  let phaseName: string;
  if (phase < 0.025 || phase >= 0.975) phaseName = 'new-moon';
  else if (phase < 0.225) phaseName = 'waxing-crescent';
  else if (phase < 0.275) phaseName = 'first-quarter';
  else if (phase < 0.475) phaseName = 'waxing-gibbous';
  else if (phase < 0.525) phaseName = 'full-moon';
  else if (phase < 0.725) phaseName = 'waning-gibbous';
  else if (phase < 0.775) phaseName = 'last-quarter';
  else phaseName = 'waning-crescent';

  return { phase, phaseName, illumination };
}

// Calculate days until the next full moon using accurate search
function getDaysUntilFullMoon(date: Date): number {
  const currentPhase = calculateMoonPhase(date);

  // Search forward day by day for next full moon
  let daysUntil = 0;
  const maxDays = 30; // Synodic month is ~29.5 days

  for (let i = 1; i <= maxDays; i++) {
    const futureDate = new Date(date.getTime() + i * 24 * 60 * 60 * 1000);
    const futurePhase = calculateMoonPhase(futureDate);

    // Check if we crossed the full moon (phase near 0.5)
    if (futurePhase.phase >= 0.475 && futurePhase.phase <= 0.525) {
      daysUntil = i;
      break;
    }

    // Handle wrap-around: if current phase > 0.5, we need to wait for next cycle
    if (currentPhase.phase > 0.5 && futurePhase.phase < currentPhase.phase && futurePhase.phase >= 0.475) {
      daysUntil = i;
      break;
    }
  }

  // If still 0, calculate based on synodic month
  if (daysUntil === 0) {
    const synodicMonth = 29.53058867;
    if (currentPhase.phase <= 0.5) {
      daysUntil = Math.round((0.5 - currentPhase.phase) * synodicMonth);
    } else {
      daysUntil = Math.round((1.5 - currentPhase.phase) * synodicMonth);
    }
  }

  return daysUntil;
}

// Calculate sidereal time at Greenwich
function getGreenwichSiderealTime(jd: number): number {
  const T = (jd - 2451545.0) / 36525;
  let gst = 280.46061837 + 360.98564736629 * (jd - 2451545.0) + 0.000387933 * T * T;
  return normalize360(gst);
}

// Calculate moonrise and moonset times using accurate algorithm
function calculateMoonTimes(date: Date, lat: number, lng: number): { moonrise: Date; moonset: Date } {
  const h0 = 0.125; // Moon's apparent radius in degrees (average)
  const latRad = toRad(lat);

  // Start from midnight local time
  const midnight = new Date(date);
  midnight.setHours(0, 0, 0, 0);

  let moonrise: Date | null = null;
  let moonset: Date | null = null;

  // Search through the day in 10-minute intervals
  const interval = 10; // minutes
  let prevAltitude: number | null = null;

  for (let minutes = 0; minutes <= 24 * 60; minutes += interval) {
    const checkTime = new Date(midnight.getTime() + minutes * 60 * 1000);
    const jd = getJulianDate(checkTime);

    const moon = getMoonPosition(jd);
    const gst = getGreenwichSiderealTime(jd);

    // Local sidereal time
    const lst = normalize360(gst + lng);

    // Hour angle
    const ha = toRad(lst - moon.rightAscension);
    const decRad = toRad(moon.declination);

    // Calculate altitude
    const altitude = toDeg(Math.asin(
      Math.sin(latRad) * Math.sin(decRad) +
      Math.cos(latRad) * Math.cos(decRad) * Math.cos(ha)
    ));

    if (prevAltitude !== null) {
      // Check for rising (crossing from below to above horizon)
      if (prevAltitude < h0 && altitude >= h0 && !moonrise) {
        // Interpolate for more accurate time
        const fraction = (h0 - prevAltitude) / (altitude - prevAltitude);
        moonrise = new Date(midnight.getTime() + (minutes - interval + fraction * interval) * 60 * 1000);
      }

      // Check for setting (crossing from above to below horizon)
      if (prevAltitude >= h0 && altitude < h0 && !moonset) {
        const fraction = (prevAltitude - h0) / (prevAltitude - altitude);
        moonset = new Date(midnight.getTime() + (minutes - interval + fraction * interval) * 60 * 1000);
      }
    }

    prevAltitude = altitude;
  }

  // If no rise/set found (circumpolar or moon below horizon all day), provide estimates
  if (!moonrise || !moonset) {
    const phase = calculateMoonPhase(date);

    // Fallback: estimate based on phase
    const baseHour = 6 + phase.phase * 24; // New moon ~6am, full moon ~6pm rise

    if (!moonrise) {
      moonrise = new Date(midnight.getTime() + (baseHour % 24) * 60 * 60 * 1000);
    }
    if (!moonset) {
      moonset = new Date(midnight.getTime() + ((baseHour + 12) % 24) * 60 * 60 * 1000);
    }
  }

  return { moonrise, moonset };
}

// Calculate sunrise, sunset, and twilight times using simplified solar calculations
function calculateSunTimes(date: Date, lat: number, lng: number): { sunrise: Date; sunset: Date; firstLight: Date; lastLight: Date } {
  const dayOfYear = Math.floor((date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / 86400000);

  // Solar declination
  const declination = 23.45 * Math.sin((2 * Math.PI / 365) * (dayOfYear - 81));
  const declinationRad = declination * (Math.PI / 180);
  const latRad = lat * (Math.PI / 180);

  // Hour angle for sunrise/sunset (sun at horizon, 0°)
  const cosHourAngle = -Math.tan(latRad) * Math.tan(declinationRad);
  const hourAngle = Math.acos(Math.max(-1, Math.min(1, cosHourAngle))) * (180 / Math.PI);

  // Hour angle for civil twilight (sun 6° below horizon)
  const civilTwilightAngle = 6 * (Math.PI / 180); // 6 degrees in radians
  const cosTwilightHourAngle = (Math.sin(-civilTwilightAngle) - Math.sin(latRad) * Math.sin(declinationRad)) / (Math.cos(latRad) * Math.cos(declinationRad));
  const twilightHourAngle = Math.acos(Math.max(-1, Math.min(1, cosTwilightHourAngle))) * (180 / Math.PI);

  // Solar noon (in hours, UTC)
  const solarNoon = 12 - (lng / 15);

  // Sunrise and sunset times in hours (UTC)
  const sunriseHours = solarNoon - (hourAngle / 15);
  const sunsetHours = solarNoon + (hourAngle / 15);

  // Civil twilight times (first light = dawn, last light = dusk)
  const firstLightHours = solarNoon - (twilightHourAngle / 15);
  const lastLightHours = solarNoon + (twilightHourAngle / 15);

  // Convert to local time
  const timezoneOffset = -date.getTimezoneOffset() / 60;

  const sunrise = new Date(date);
  sunrise.setHours(0, 0, 0, 0);
  sunrise.setMinutes((sunriseHours + timezoneOffset) * 60);

  const sunset = new Date(date);
  sunset.setHours(0, 0, 0, 0);
  sunset.setMinutes((sunsetHours + timezoneOffset) * 60);

  const firstLight = new Date(date);
  firstLight.setHours(0, 0, 0, 0);
  firstLight.setMinutes((firstLightHours + timezoneOffset) * 60);

  const lastLight = new Date(date);
  lastLight.setHours(0, 0, 0, 0);
  lastLight.setMinutes((lastLightHours + timezoneOffset) * 60);

  return { sunrise, sunset, firstLight, lastLight };
}

function useIsMobile(breakpoint = 720) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
}

const HeaderClock: React.FC = () => {
  const [time, setTime] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [showMoonView, setShowMoonView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const translate = useTranslate();
  const { keycloak } = useKeycloak();

  // Get language from keycloak user settings, fallback to 'de'
  const currentLanguage = (keycloak?.idTokenParsed?.['locale'] as string) || 'de';

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const coordinates = LANGUAGE_COORDINATES[currentLanguage] || LANGUAGE_COORDINATES.de;
  const sunTimes = useMemo(() => calculateSunTimes(time, coordinates.lat, coordinates.lng), [time, coordinates]);
  const moonTimes = useMemo(() => calculateMoonTimes(time, coordinates.lat, coordinates.lng), [time, coordinates]);
  const moonPhase = useMemo(() => calculateMoonPhase(time), [time]);

  const dateStr = time.toLocaleDateString(currentLanguage, { weekday: 'short', day: 'numeric', month: 'short' });
  const timeStr = time.toLocaleTimeString(currentLanguage, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const formatSunTime = (date: Date) => date.toLocaleTimeString(currentLanguage, { hour: '2-digit', minute: '2-digit' });

  // Calculate daylight duration
  const daylightMs = sunTimes.sunset.getTime() - sunTimes.sunrise.getTime();
  const daylightHours = Math.floor(daylightMs / 3600000);
  const daylightMinutes = Math.floor((daylightMs % 3600000) / 60000);

  // DST info
  const hasDST = coordinates.hasDST;
  const isSummerTime = isDST(time);
  const nextDSTChange = hasDST ? getNextDSTChange(time) : null;

  const formatDSTDate = (date: Date) => date.toLocaleDateString(currentLanguage, { day: 'numeric', month: 'short' });

  return (
    <div className="neo-header-clock-container" ref={containerRef}>
      <button type="button" className="neo-header-clock" onClick={() => setIsOpen(!isOpen)}>
        <span className="neo-header-clock-date">{dateStr}</span>
        <span className="neo-header-clock-time">{timeStr}</span>
      </button>

      {isOpen && (
        <div className="neo-sun-dropdown">
          {/* Toggle button in top right */}
          <button
            type="button"
            className="neo-sky-toggle-btn"
            onClick={() => setShowMoonView(!showMoonView)}
            title={showMoonView ? translate('show-sun') : translate('show-moon')}
          >
            {showMoonView ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
              </svg>
            )}
          </button>

          {!showMoonView ? (
            <>
              <DaylightArc
                sunrise={sunTimes.sunrise}
                sunset={sunTimes.sunset}
                currentTime={time}
                formatTime={formatSunTime}
              />
              <div className="neo-sun-footer">
                <div className="neo-sun-times-row">
                  <span className="neo-sun-time-item">
                    <span className="neo-sun-time-label">{translate('first-light')}</span>
                    <span className="neo-sun-time-value">{formatSunTime(sunTimes.firstLight)}</span>
                  </span>
                  <span className="neo-sun-time-item">
                    <span className="neo-sun-time-label">{translate('daylight')}</span>
                    <span className="neo-sun-time-value">{daylightHours}h {daylightMinutes}m</span>
                  </span>
                  <span className="neo-sun-time-item">
                    <span className="neo-sun-time-label">{translate('last-light')}</span>
                    <span className="neo-sun-time-value">{formatSunTime(sunTimes.lastLight)}</span>
                  </span>
                </div>
                {hasDST && nextDSTChange && (
                  <div className="neo-sun-times-row neo-sun-dst-row">
                    <span className="neo-sun-time-item">
                      <span className="neo-sun-time-label">{translate('time-type')}</span>
                      <span className="neo-sun-time-value">{translate(isSummerTime ? 'summer-time' : 'winter-time')}</span>
                    </span>
                    <span className="neo-sun-time-item">
                      <span className="neo-sun-time-label">{translate('change')}</span>
                      <span className="neo-sun-time-value">{formatDSTDate(nextDSTChange.date)}</span>
                    </span>
                    <span className="neo-sun-time-item">
                      <span className="neo-sun-time-label">{translate('shift')}</span>
                      <span className="neo-sun-time-value">{nextDSTChange.isSummerToWinter ? '-1h' : '+1h'}</span>
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <MoonPhaseArc
                moonrise={moonTimes.moonrise}
                moonset={moonTimes.moonset}
                currentTime={time}
                formatTime={formatSunTime}
              />
              <div className="neo-sun-footer">
                {(() => {
                  // Calculate moonlight duration (handle overnight case)
                  let moonlightMs = moonTimes.moonset.getTime() - moonTimes.moonrise.getTime();
                  if (moonlightMs < 0) moonlightMs += 24 * 60 * 60 * 1000; // Add 24h if moonset is next day
                  const moonlightHours = Math.floor(moonlightMs / 3600000);
                  const moonlightMinutes = Math.floor((moonlightMs % 3600000) / 60000);
                  return (
                    <>
                      <div className="neo-sun-times-row">
                        <span className="neo-sun-time-item">
                          <span className="neo-sun-time-label">{translate('moonrise')}</span>
                          <span className="neo-sun-time-value">{formatSunTime(moonTimes.moonrise)}</span>
                        </span>
                        <span className="neo-sun-time-item">
                          <span className="neo-sun-time-label">{translate('moonlight')}</span>
                          <span className="neo-sun-time-value">{moonlightHours}h {moonlightMinutes}m</span>
                        </span>
                        <span className="neo-sun-time-item">
                          <span className="neo-sun-time-label">{translate('moonset')}</span>
                          <span className="neo-sun-time-value">{formatSunTime(moonTimes.moonset)}</span>
                        </span>
                      </div>
                      <div className="neo-sun-times-row neo-sun-dst-row">
                        <span className="neo-sun-time-item">
                          <span className="neo-sun-time-label">{translate('moon-phase')}</span>
                          <span className="neo-sun-time-value">{translate(moonPhase.phaseName)}</span>
                        </span>
                        <span className="neo-sun-time-item">
                          <span className="neo-sun-time-label">{translate('illumination')}</span>
                          <span className="neo-sun-time-value">{Math.round(moonPhase.illumination * 100)}%</span>
                        </span>
                        <span className="neo-sun-time-item">
                          <span className="neo-sun-time-label">{translate('next-full-moon')}</span>
                          <span className="neo-sun-time-value">{getDaysUntilFullMoon(time)} {translate('days')}</span>
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const { keycloak } = useKeycloak();
  const isMobile = useIsMobile(720);

  if (!isMobile) {
    return (
      <header className="neo-header">
        <nav className="neo-header-nav" aria-label="Global">
          <div className="neo-header-logo" id="logo-in-header">
            <SipLogo isAuthenticated={keycloak.authenticated} />
          </div>

          <div className="neo-header-breadcrumbs">
            <Breadcrumbs />
          </div>

          <div className="neo-header-button-row">
            {!keycloak.authenticated && location.pathname.includes('salamander-info') && (
              <>
                <LoginButton keycloak={keycloak} />
              </>
            )}
            {keycloak.authenticated && (
              <>
                <SpotlightTrigger />
                <ExtensionSlot name="notifications" />
                <ControlCenter keycloak={keycloak} />
                <ProfileButton keycloak={keycloak} />
                <HeaderClock />
              </>
            )}
          </div>
        </nav>
      </header>
    );
  }
};
